import React from "react";
import {
  AlertTriangle, Brain, CheckCircle2, ClipboardList, Download, FileCode2, Loader2, Minus, ShieldCheck, TrendingDown, TrendingUp,
} from "lucide-react";
import * as api from "../api/cyber";
import * as traApi from "../api/tra";
import {
  RiskMatrix, ExposureTreemap, RunwayTimeline, BlastRadius, WeaknessProfile, TrendChart,
} from "../features/cyber/charts";
import {
  card, th, td, btn, SEV, Kpi, Pill, Provenance, useAsync, Loading, Err, n,
} from "./cyberShared";

/**
 * The compliance and governance half of the Cyber section: SBOM coverage,
 * authorisation and POA&M, the AI use-case inventory, and posture over time.
 *
 * Split out of CyberPage so neither file becomes the kind of component nobody
 * wants to open.
 */


/* ---------------------------------------------------------------- risk map */
export function RiskMap() {
  const [metric, setMetric] = React.useState("devices");
  const products = useAsync(() => api.getProducts(), []);
  const vulns = useAsync(() => api.getVulnerabilities({ limit: 2000 }), []);

  if (products.loading || vulns.loading) return <Loading />;
  if (products.error) return <Err>{products.error}</Err>;
  if (vulns.error) return <Err>{vulns.error}</Err>;

  const rows = vulns.data.vulnerabilities;
  const kev = rows.filter((v) => v.knownExploited);
  // The count that justifies the whole chart: findings an ordinary severity sort
  // would bury, because CVSS alone says they are unremarkable.
  const sleepers = rows.filter((v) => v.epss >= 0.5 && v.cvss < 7).length;

  return (
    <div>
      <Provenance provenance={vulns.data.provenance} compact />

      <div style={{ ...card, padding: 16, marginBottom: 14 }}>
        <RiskMatrix vulnerabilities={rows} />
        {sleepers > 0 && (
          <p style={{ fontSize: 11.5, color: "var(--cares-amber)", background: "color-mix(in srgb, var(--cares-amber) 14%, var(--cares-surface-card))",
                      border: "1px solid color-mix(in srgb, var(--cares-amber) 30%, var(--cares-surface-card))", borderRadius: 8, padding: 9,
                      marginTop: 10 }}>
            <strong>{sleepers} findings sit below CVSS 7 but above 50% exploitation
            probability.</strong> A severity-ordered worklist puts them near the
            bottom; this is the chart that finds them.
          </p>
        )}
      </div>

      <div style={{ ...card, padding: 16, marginBottom: 14 }}>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 6, marginBottom: -6 }}>
          {[["devices", "By devices"], ["spend", "By spend"]].map(([k, label]) => (
            <button key={k} onClick={() => setMetric(k)}
                    style={{ ...btn, padding: "4px 9px", fontSize: 11,
                             background: metric === k ? "color-mix(in srgb, var(--cares-purple) 14%, var(--cares-surface-card))" : "var(--cares-surface-card)",
                             borderColor: metric === k ? "color-mix(in srgb, var(--cares-purple) 30%, var(--cares-surface-card))" : "var(--cares-line-strong)",
                             color: metric === k ? "var(--cares-purple)" : "var(--cares-text-2)" }}>
              {label}
            </button>
          ))}
        </div>
        <ExposureTreemap products={products.data.products} metric={metric} />
      </div>

      <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
        <div style={{ ...card, padding: 16, flex: "1 1 400px", minWidth: 0 }}>
          <WeaknessProfile vulnerabilities={rows} />
        </div>
        <div style={{ ...card, padding: 16, flex: "1 1 260px", minWidth: 0 }}>
          <h3 style={{ fontSize: 13, fontWeight: 700, margin: "0 0 10px" }}>
            Reading the matrix
          </h3>
          <p style={{ fontSize: 11.5, color: "var(--cares-text-3)", lineHeight: 1.6, margin: 0 }}>
            CVSS says how bad a vulnerability would be. EPSS says how likely anyone
            is to try it in the next month. They disagree often, and severity alone
            is what produces a queue where a theoretical 9.8 outranks a 7.2 that is
            being exploited today.
          </p>
          <p style={{ fontSize: 11.5, color: "var(--cares-text-3)", lineHeight: 1.6, marginTop: 9 }}>
            Bubble size is the device count behind each finding, so the top-right
            corner is not just "severe and likely" but "severe, likely, and
            everywhere".
          </p>
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--cares-line)",
                        fontSize: 11.5, color: "var(--cares-text-3)" }}>
            <strong style={{ color: "var(--cares-text-1)" }}>{kev.length} findings</strong> are on
            the CISA known-exploited catalogue — those carry a legal deadline rather
            than a risk score, and appear in red regardless of where they plot.
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------- SBOM */
export function Sbom() {
  const { loading, data, error } = useAsync(() => api.getSbom(), []);
  if (loading) return <Loading />;
  if (error) return <Err>{error}</Err>;
  const t = data.totals;

  return (
    <div>
      <Provenance provenance={data.provenance} compact />
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
        <Kpi label="SBOM coverage" value={`${t.coveragePct}%`} icon={FileCode2}
             tone={t.coveragePct < 80 ? "var(--cares-amber)" : "var(--cares-green)"}
             sub={`${t.documents} held, ${t.productsWithout} missing`} />
        <Kpi label="Components tracked" value={n(t.components)}
             sub={`${t.vulnerableComponents} known vulnerable`} />
        <Kpi label="Vendor-attested" value={t.vendorAttested}
             sub={`${t.documents - t.vendorAttested} generated by scanning`} />
        <Kpi label="Cryptographically signed" value={t.signed}
             tone={t.signed < t.documents / 2 ? "var(--cares-amber)" : "var(--cares-green)"}
             sub="tamper-evident bills of materials" />
      </div>

      {t.productsWithout > 0 && (
        <div style={{ ...card, background: "color-mix(in srgb, var(--cares-amber) 14%, var(--cares-surface-card))", borderColor: "color-mix(in srgb, var(--cares-amber) 30%, var(--cares-surface-card))", padding: 12,
                      marginBottom: 14, fontSize: 12, color: "var(--cares-amber)",
                      display: "flex", gap: 9 }}>
          <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>
            <strong>{t.productsWithout} products have no bill of materials.</strong>{" "}
            When the next widely-used library turns out to be vulnerable, "are we
            affected?" cannot be answered for these:{" "}
            {data.productsWithoutSbom.map((p) => p.product).join(", ")}.
          </span>
        </div>
      )}

      <div style={{ ...card, padding: 16, marginBottom: 14 }}>
        <BlastRadius shared={data.sharedComponents} />
      </div>

      <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
        <div style={{ ...card, flex: "1 1 320px", overflow: "hidden" }}>
          <h3 style={{ fontSize: 13, fontWeight: 700, margin: 0, padding: "13px 14px 6px" }}>
            Most widely shared components
          </h3>
          <p style={{ fontSize: 11, color: "var(--cares-text-note)", margin: "0 14px 8px" }}>
            One advisory against any of these becomes an estate-wide incident.
          </p>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>
              <th style={th}>Component</th><th style={th}>Ecosystem</th>
              <th style={th}>In products</th>
            </tr></thead>
            <tbody>
              {data.sharedComponents.map((c) => (
                <tr key={c.name + c.ecosystem}>
                  <td style={td}>
                    <span style={{ fontFamily: "ui-monospace, Menlo, monospace",
                                   fontSize: 11 }}>{c.name}</span>
                    {c.vulnerable && (
                      <Pill tone={SEV.HIGH}>&nbsp;vulnerable&nbsp;</Pill>
                    )}
                  </td>
                  <td style={td}>{c.ecosystem}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{c.products}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ ...card, flex: "1 1 420px", overflow: "hidden" }}>
          <h3 style={{ fontSize: 13, fontWeight: 700, margin: 0, padding: "13px 14px 9px" }}>
            Bills of materials held
          </h3>
          <div style={{ overflowX: "auto", maxHeight: 420 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>
                <th style={th}>Product</th><th style={th}>Format</th>
                <th style={th}>Components</th><th style={th}>Vulnerable</th>
                <th style={th}>Source</th>
              </tr></thead>
              <tbody>
                {data.documents.map((d) => (
                  <tr key={d.sbom_id}>
                    <td style={td}>
                      <div style={{ fontWeight: 600, color: "var(--cares-text-1)" }}>{d.product_name}</div>
                      <div style={{ fontSize: 10, color: "var(--cares-text-note)" }}>{d.supplier}</div>
                    </td>
                    <td style={{ ...td, fontSize: 11 }}>
                      {d.bom_format} {d.spec_version}
                      {d.signed && (
                        <div style={{ fontSize: 9.5, color: "var(--cares-green)" }}>signed</div>
                      )}
                    </td>
                    <td style={td}>{d.component_count}</td>
                    <td style={{ ...td, color: d.vulnerable_components ? "var(--cares-orange)" : "var(--cares-text-note)",
                                 fontWeight: d.vulnerable_components ? 700 : 400 }}>
                      {d.vulnerable_components || "—"}
                    </td>
                    <td style={{ ...td, fontSize: 10.5,
                                 color: d.source === "vendor-attested" ? "var(--cares-green)" : "var(--cares-amber)" }}>
                      {d.source}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------- authorisation */
export function Authorisation() {
  const [overdueOnly, setOverdueOnly] = React.useState(false);
  const [status, setStatus] = React.useState("OPEN,ONGOING");
  const [saving, setSaving] = React.useState(null);
  const [tick, setTick] = React.useState(0);

  const ato = useAsync(() => api.getAto(), [tick]);
  const poam = useAsync(() => api.getPoam({ status: status || undefined, overdueOnly }),
                        [status, overdueOnly, tick]);

  const work = async (id, next) => {
    setSaving(id);
    try { await api.updatePoam(id, next); setTick((t) => t + 1); }
    finally { setSaving(null); }
  };

  return (
    <div>
      {ato.loading ? <Loading /> : ato.error ? <Err>{ato.error}</Err> : (
        <>
          <Provenance provenance={ato.data.provenance} compact />
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
            <Kpi label="Authorisations" value={ato.data.count} icon={ShieldCheck}
                 sub={`${ato.data.authorized} currently authorised`} />
            <Kpi label="Expired" value={ato.data.expired}
                 tone={ato.data.expired ? "var(--cares-orange)" : "var(--cares-green)"}
                 sub="operating without current authority" />
            <Kpi label="Expiring within 90 days" value={ato.data.expiringWithin90Days}
                 tone={ato.data.expiringWithin90Days ? "var(--cares-amber)" : "var(--cares-green)"} />
            <Kpi label="Continuous monitoring" value={ato.data.continuousMonitoring}
                 sub="ongoing authorisation" />
          </div>

          {ato.data.expired > 0 && (
            <div style={{ ...card, background: "color-mix(in srgb, var(--cares-orange) 14%, var(--cares-surface-card))", borderColor: "color-mix(in srgb, var(--cares-orange) 30%, var(--cares-surface-card))", padding: 12,
                          marginBottom: 14, fontSize: 12, color: "var(--cares-orange)",
                          display: "flex", gap: 9 }}>
              <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>
                <strong>{ato.data.expired} systems have an expired ATO.</strong>{" "}
                An expired authorisation is a finding in its own right — the system is
                operating without current authority to do so.
              </span>
            </div>
          )}

          <div style={{ ...card, overflow: "hidden", marginBottom: 18 }}>
            <div style={{ overflowX: "auto", maxHeight: 300 }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr>
                  <th style={th}>System</th><th style={th}>Type</th><th style={th}>Status</th>
                  <th style={th}>FIPS 199</th><th style={th}>Baseline</th>
                  <th style={th}>Expires</th><th style={th}>Open POA&amp;Ms</th>
                  <th style={th}>Authorising official</th>
                </tr></thead>
                <tbody>
                  {ato.data.records.map((r) => (
                    <tr key={r.ato_id} style={{ background: r.expired ? "color-mix(in srgb, var(--cares-orange) 14%, var(--cares-surface-card))" : undefined }}>
                      <td style={td}>
                        <div style={{ fontWeight: 600, color: "var(--cares-text-1)" }}>{r.system_name}</div>
                        <div style={{ fontSize: 10, color: "var(--cares-text-note)" }}>
                          {r.system_acronym} · {r.system_owner}
                        </div>
                      </td>
                      <td style={{ ...td, fontSize: 11 }}>{r.ato_type}</td>
                      <td style={td}>
                        <Pill tone={r.status === "EXPIRED" ? SEV.CRITICAL
                                    : r.status === "AUTHORIZED" ? { bg: "color-mix(in srgb, var(--cares-green) 22%, var(--cares-surface-card))", fg: "var(--cares-green)" }
                                    : SEV.MEDIUM}>{r.status}</Pill>
                      </td>
                      <td style={td}>{r.fips199}</td>
                      <td style={{ ...td, fontSize: 10.5 }}>{r.control_baseline}</td>
                      <td style={{ ...td, color: r.expired ? "var(--cares-orange)" : "var(--cares-text-3)" }}>
                        {r.expiration_date}
                        <div style={{ fontSize: 9.5, color: "var(--cares-text-note)" }}>
                          {r.daysToExpiry != null &&
                            (r.daysToExpiry < 0 ? `${Math.abs(r.daysToExpiry)}d ago`
                                                : `in ${r.daysToExpiry}d`)}
                        </div>
                      </td>
                      <td style={td}>{r.open_poams || "—"}</td>
                      <td style={{ ...td, fontSize: 11 }}>{r.authorizing_official}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 10,
                    flexWrap: "wrap" }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, margin: 0, flex: 1,
                     display: "flex", alignItems: "center", gap: 7 }}>
          <ClipboardList size={15} style={{ color: "var(--cares-purple)" }} />
          Plan of Action &amp; Milestones
        </h3>
        <select value={status} onChange={(e) => setStatus(e.target.value)}
                style={{ ...btn, cursor: "pointer" }}>
          <option value="OPEN,ONGOING">Open &amp; ongoing</option>
          <option value="">All statuses</option>
          <option value="COMPLETED">Completed</option>
          <option value="RISK_ACCEPTED">Risk accepted</option>
        </select>
        <label style={{ ...btn, cursor: "pointer",
                        background: overdueOnly ? "color-mix(in srgb, var(--cares-orange) 22%, var(--cares-surface-card))" : "var(--cares-surface-card)",
                        color: overdueOnly ? "var(--cares-orange)" : "var(--cares-text-2)" }}>
          <input type="checkbox" checked={overdueOnly}
                 onChange={(e) => setOverdueOnly(e.target.checked)} />
          Overdue only
        </label>
      </div>

      {poam.loading ? <Loading /> : poam.error ? <Err>{poam.error}</Err> : (
        <>
          <div style={{ fontSize: 11.5, color: "var(--cares-text-label)", marginBottom: 8 }}>
            {Object.entries(poam.data.byStatus).map(([k, v]) =>
              `${k}: ${v.count}${v.overdue ? ` (${v.overdue} overdue)` : ""}`).join(" · ")}
          </div>
          <div style={{ ...card, overflow: "hidden" }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr>
                  <th style={th}>Ref</th><th style={th}>Weakness</th>
                  <th style={th}>Source</th><th style={th}>Control</th>
                  <th style={th}>Severity</th><th style={th}>Due</th>
                  <th style={th}>Milestones</th><th style={th}>Status</th>
                </tr></thead>
                <tbody>
                  {poam.data.items.map((r) => (
                    <tr key={r.poam_id} style={{ background: r.overdue ? "color-mix(in srgb, var(--cares-orange) 14%, var(--cares-surface-card))" : undefined }}>
                      <td style={{ ...td, fontFamily: "ui-monospace, monospace",
                                   fontSize: 10.5, whiteSpace: "nowrap" }}>{r.poam_ref}</td>
                      <td style={{ ...td, maxWidth: 300 }}>
                        <div style={{ fontSize: 11.5 }}>{r.weakness}</div>
                        <div style={{ fontSize: 10, color: "var(--cares-text-note)" }}>{r.product_name}</div>
                      </td>
                      <td style={{ ...td, fontFamily: "ui-monospace, monospace",
                                   fontSize: 10 }}>
                        {r.source_identifier}
                        <div style={{ fontFamily: "inherit", fontSize: 9.5,
                                      color: r.source_type === "KEV" ? "var(--cares-orange)" : "var(--cares-text-note)",
                                      fontWeight: r.source_type === "KEV" ? 700 : 400 }}>
                          {r.source_type}
                        </div>
                      </td>
                      <td style={td}>{r.nist_control}</td>
                      <td style={td}><Pill tone={SEV[r.severity] || SEV.NONE}>{r.severity}</Pill></td>
                      <td style={{ ...td, color: r.overdue ? "var(--cares-orange)" : "var(--cares-text-3)",
                                   fontWeight: r.overdue ? 700 : 400, whiteSpace: "nowrap" }}>
                        {r.scheduled_completion}
                        {r.overdue && (
                          <div style={{ fontSize: 9.5 }}>{Math.abs(r.daysToDue)}d overdue</div>
                        )}
                      </td>
                      <td style={td}>
                        <span style={{ fontSize: 11 }}>
                          {r.milestonesComplete}/{r.milestones.length}
                        </span>
                        <div style={{ height: 4, width: 52, background: "var(--cares-surface-board)",
                                      borderRadius: 2, marginTop: 3 }}>
                          <div style={{ height: "100%", borderRadius: 2, background: "var(--cares-purple)",
                                        width: `${r.milestones.length
                                          ? (r.milestonesComplete / r.milestones.length) * 100
                                          : 0}%` }} />
                        </div>
                      </td>
                      <td style={td}>
                        <select value={r.status} disabled={saving === r.poam_id}
                                onChange={(e) => work(r.poam_id, e.target.value)}
                                style={{ fontSize: 10.5, padding: "3px 5px", borderRadius: 6,
                                         border: "1px solid var(--cares-line-strong)", cursor: "pointer" }}>
                          {["OPEN", "ONGOING", "COMPLETED", "RISK_ACCEPTED", "DEFERRED"]
                            .map((sv) => <option key={sv} value={sv}>{sv}</option>)}
                        </select>
                        {saving === r.poam_id && <Loader2 size={11} />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* -------------------------------------------------------------- AI systems */
export function AiSystems() {
  const { loading, data, error } = useAsync(() => api.getAiSystems(), []);
  if (loading) return <Loading />;
  if (error) return <Err>{error}</Err>;
  const s = data.summary;

  return (
    <div>
      <Provenance provenance={data.provenance} compact />
      {/* A governance board needs this as a file it can attach to a submission,
          not a screen it has to retype. The columns follow the OMB M-25-21
          inventory, so the download is the deliverable rather than a summary. */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
        <a href={api.aiInventoryExportUrl("csv")}
           style={{ display: "inline-flex", alignItems: "center", gap: 6,
                    fontSize: 12, fontWeight: 600, color: "var(--cares-purple)",
                    textDecoration: "none", border: "1px solid color-mix(in srgb, var(--cares-purple) 30%, var(--cares-surface-card))",
                    background: "color-mix(in srgb, var(--cares-purple) 14%, var(--cares-surface-card))", padding: "6px 11px", borderRadius: 7 }}>
          <Download size={13} /> Export AI use-case inventory (OMB M-25-21 CSV)
        </a>
      </div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
        <Kpi label="AI systems" value={s.systems} icon={Brain}
             sub={`${s.regulated} carry mandatory practices`} />
        <Kpi label="Rights-impacting" value={s.rightsImpacting} tone="var(--cares-orange)"
             sub="require opt-out and human alternative" />
        <Kpi label="Safety-impacting" value={s.safetyImpacting} tone="var(--cares-orange)" />
        <Kpi label="Missing impact assessment" value={s.missingImpactAssessment}
             tone={s.missingImpactAssessment ? "var(--cares-amber)" : "var(--cares-green)"}
             sub={`${s.missingRealWorldTest} untested in a real-world context`} />
      </div>

      <div style={{ ...card, background: "var(--cares-surface-board)", padding: 12, marginBottom: 14,
                    fontSize: 12, color: "var(--cares-text-3)", display: "flex", gap: 9 }}>
        <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1, color: "var(--cares-amber)" }} />
        <span>{s.estateFlagAgreement.note}</span>
      </div>

      <div style={{ ...card, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>
              <th style={th}>System</th><th style={th}>Capability</th>
              <th style={th}>Category</th><th style={th}>Data handling</th>
              <th style={th}>Human oversight</th><th style={th}>Gaps</th>
              <th style={th}>Host exposure</th>
            </tr></thead>
            <tbody>
              {data.systems.map((r) => (
                <tr key={r.productId}>
                  <td style={td}>
                    <div style={{ fontWeight: 600, color: "var(--cares-text-1)" }}>{r.system}</div>
                    <div style={{ fontSize: 10, color: "var(--cares-text-note)" }}>
                      {r.vendor} · {n(r.devices)} devices
                    </div>
                  </td>
                  <td style={{ ...td, fontSize: 11, maxWidth: 210 }}>
                    <div style={{ fontWeight: 600 }}>{r.capability}</div>
                    <div style={{ color: "var(--cares-text-note)", fontSize: 10.5 }}>{r.useCase}</div>
                  </td>
                  <td style={td}>
                    <Pill tone={r.category === "Neither" ? SEV.NONE
                                : r.rightsImpacting ? SEV.CRITICAL : SEV.HIGH}>
                      {r.category}
                    </Pill>
                  </td>
                  <td style={{ ...td, fontSize: 10.5, maxWidth: 190 }}>{r.dataHandling}</td>
                  <td style={{ ...td, fontSize: 10.5, maxWidth: 170 }}>{r.humanOversight}</td>
                  <td style={td}>
                    {r.gaps.length === 0 ? (
                      <CheckCircle2 size={14} style={{ color: "var(--cares-green)" }} />
                    ) : (
                      <div>
                        {r.gaps.map((g) => (
                          <div key={g} style={{ fontSize: 10, color: "var(--cares-amber)" }}>• {g}</div>
                        ))}
                      </div>
                    )}
                  </td>
                  <td style={{ ...td, fontWeight: 700,
                               color: r.hostBand === "HIGH" ? "var(--cares-orange)" : "var(--cares-text-3)" }}>
                    {r.hostExposure}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ trends */
export function Trends() {
  const [days, setDays] = React.useState(120);
  const { loading, data, error } = useAsync(() => api.getTrends(days), [days]);
  if (loading) return <Loading />;
  if (error) return <Err>{error}</Err>;
  if (!data.count) return <Err>{data.message}</Err>;

  const Change = ({ label, field, unit = "", invert = false }) => {
    const c = data.change[field];
    if (!c) return null;
    const good = invert ? c.change > 0 : c.change < 0;
    const Icon = c.change === 0 ? Minus : good ? TrendingDown : TrendingUp;
    return (
      <div style={{ ...card, padding: 14, flex: "1 1 168px", minWidth: 168 }}>
        <div style={{ fontSize: 10.5, fontWeight: 700, color: "var(--cares-text-note)",
                      textTransform: "uppercase", letterSpacing: .4 }}>{label}</div>
        <div style={{ fontSize: 23, fontWeight: 700, marginTop: 6, color: "var(--cares-text-1)" }}>
          {c.to}{unit}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11,
                      marginTop: 3, color: c.change === 0 ? "var(--cares-text-note)"
                                          : good ? "var(--cares-green)" : "var(--cares-orange)" }}>
          <Icon size={12} />
          {c.change > 0 ? "+" : ""}{c.change}{unit} from {c.from}{unit}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 12, alignItems: "center" }}>
        <span style={{ fontSize: 12, color: "var(--cares-text-label)" }}>Window:</span>
        {[30, 60, 120].map((d) => (
          <button key={d} onClick={() => setDays(d)}
                  style={{ ...btn, background: days === d ? "color-mix(in srgb, var(--cares-purple) 14%, var(--cares-surface-card))" : "var(--cares-surface-card)",
                           borderColor: days === d ? "color-mix(in srgb, var(--cares-purple) 30%, var(--cares-surface-card))" : "var(--cares-line-strong)",
                           color: days === d ? "var(--cares-purple)" : "var(--cares-text-2)" }}>
            {d} days
          </button>
        ))}
      </div>

      <Provenance provenance={data.provenance} compact />

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
        <Change label="Open findings" field="open_findings" />
        <Change label="Exploited open" field="kev_open" />
        <Change label="Mean time to remediate" field="mttr_days" unit="d" />
        <Change label="MTTR, exploited" field="mttr_kev_days" unit="d" />
        <Change label="Exposure index" field="exposure_index" />
        <Change label="POA&amp;M overdue" field="poam_overdue" />
      </div>

      <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
        <div style={{ ...card, padding: 16, flex: "1 1 380px", minWidth: 0 }}>
          <h3 style={{ fontSize: 13, fontWeight: 700, margin: "0 0 12px" }}>
            {data.window.from} → {data.window.to}
          </h3>
          <TrendChart series={data.series} fields={[
            { key: "open_findings", label: "Open findings", colour: "#6366f1" },
            { key: "kev_open", label: "Known exploited", colour: "#dc2626" },
            { key: "mttr_days", label: "MTTR (days)", colour: "#0ea5e9" },
            { key: "exposure_index", label: "Exposure index", colour: "#f59e0b" },
          ]} />
        </div>

        <div style={{ ...card, padding: 16, flex: "1 1 280px", minWidth: 0 }}>
          <h3 style={{ fontSize: 13, fontWeight: 700, margin: "0 0 6px" }}>
            Measured remediation time
          </h3>
          <div style={{ fontSize: 34, fontWeight: 700, color: "var(--cares-text-1)" }}>
            {data.measuredMttr.days ?? "—"}
            <span style={{ fontSize: 15, color: "var(--cares-text-note)" }}> days</span>
          </div>
          <p style={{ fontSize: 11.5, color: "var(--cares-text-label)", marginTop: 6 }}>
            Across {data.measuredMttr.closedItems} POA&amp;M items actually closed.
            {" "}{data.measuredMttr.note}
          </p>
          <div style={{ marginTop: 14, padding: 11, background: "var(--cares-surface-board)", borderRadius: 9,
                        fontSize: 11.5, color: "var(--cares-text-3)", lineHeight: 1.55 }}>
            None of this is derivable from current state. Once a finding closes, how
            long it stayed open is gone unless it was written down that day — which
            is why posture is snapshotted daily rather than recomputed.
          </div>
        </div>
      </div>
    </div>
  );
}


// Conformance against the thirteen CMS TRA foundation principles.
//
// The gap text is given the same weight as the evidence. A conformance screen
// that only shows what passed is the one an architect stops trusting the second
// time they read it — and here the gaps are the useful part, because each one
// names the field that would close it.
export function TraConformance() {
  const { loading, data, error } = useAsync(() => traApi.getConformance(), []);
  if (loading) return <Loading />;
  if (error) return <Err>{error}</Err>;
  const s = data.summary;

  const TONE = {
    evidenced:     { fg: "var(--cares-green)", bg: "color-mix(in srgb, var(--cares-green) 14%, var(--cares-surface-card))", bd: "color-mix(in srgb, var(--cares-green) 30%, var(--cares-surface-card))", label: "Evidenced" },
    partial:       { fg: "var(--cares-amber)", bg: "color-mix(in srgb, var(--cares-amber) 14%, var(--cares-surface-card))", bd: "color-mix(in srgb, var(--cares-amber) 30%, var(--cares-surface-card))", label: "Partial" },
    not_evidenced: { fg: "var(--cares-orange)", bg: "color-mix(in srgb, var(--cares-orange) 14%, var(--cares-surface-card))", bd: "color-mix(in srgb, var(--cares-orange) 30%, var(--cares-surface-card))", label: "Not evidenced" },
  };

  return (
    <div>
      <div style={{ ...card, padding: 14, marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--cares-text-1)" }}>
          {data.source.framework}
        </div>
        <div style={{ fontSize: 11.5, color: "var(--cares-text-label)", marginTop: 3 }}>
          {data.source.section} &middot;{" "}
          <a href={data.source.url} target="_blank" rel="noreferrer"
             style={{ color: "var(--cares-purple)" }}>cms.gov/tra</a>
        </div>
        <div style={{ fontSize: 11.5, color: "var(--cares-text-3)", marginTop: 7 }}>
          {data.source.control}
        </div>
      </div>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 6 }}>
        <Kpi label="Evidenced" value={s.evidenced} tone="var(--cares-green)"
             sub={`of ${s.total} principles`} />
        <Kpi label="Partial" value={s.partial} tone="var(--cares-amber)"
             sub="evidence with a stated shortfall" />
        <Kpi label="Not evidenced" value={s.not_evidenced}
             tone={s.not_evidenced ? "var(--cares-orange)" : "var(--cares-green)"}
             sub="no data speaks to these" />
      </div>
      <div style={{ fontSize: 11, color: "var(--cares-text-note)", margin: "0 0 14px", maxWidth: 640 }}>
        {s.note}
      </div>

      <div style={{ display: "grid", gap: 10 }}>
        {data.principles.map((p) => {
          const t = TONE[p.status];
          return (
            <div key={p.id} style={{ ...card, padding: 15, borderLeft: `3px solid ${t.fg}` }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 9, flexWrap: "wrap" }}>
                <span style={{ fontFamily: "monospace", fontSize: 11, color: "var(--cares-text-note)" }}>
                  {p.id}
                </span>
                <span style={{ fontSize: 14.5, fontWeight: 700, color: "var(--cares-text-1)" }}>
                  {p.name}
                </span>
                <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px",
                               borderRadius: 999, color: t.fg, background: t.bg,
                               border: `1px solid ${t.bd}` }}>
                  {t.label}
                </span>
              </div>
              <div style={{ fontSize: 12, color: "var(--cares-text-label)", margin: "5px 0 9px" }}>
                {p.summary}
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--cares-text-1)", marginBottom: 7 }}>
                {p.metric}
              </div>
              <ul style={{ margin: 0, paddingLeft: 17, fontSize: 12, color: "var(--cares-text-3)" }}>
                {p.evidence.map((e, i) => <li key={i} style={{ marginBottom: 2 }}>{e}</li>)}
              </ul>
              {p.gap && (
                <div style={{ marginTop: 9, padding: "8px 11px", borderRadius: 5,
                              background: t.bg, border: `1px solid ${t.bd}`,
                              fontSize: 12, color: t.fg }}>
                  <strong>Gap:</strong> {p.gap}
                </div>
              )}
              <div style={{ marginTop: 8, fontFamily: "monospace", fontSize: 10.5,
                            color: "var(--cares-text-note)" }}>
                scored from {p.source}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
